import { useState } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Card,
  CardContent,
  Chip,
  Divider
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useQuery } from 'convex/react';
import { api } from '../convex/_generated/api';
import PersonIcon from '@mui/icons-material/Person';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import FilterListIcon from '@mui/icons-material/FilterList';
import { Group as ClientesIcon } from '@mui/icons-material';

export const Dashboard = () => {
  const { userData } = useAuth();
  const navigate = useNavigate();
  const publicUrl = `https://immunoworld.net/${userData?.url}`;
  const contacts = useQuery(api.contacts.listContacts) ?? [];

  // Estadísticas
  const totalContacts = contacts.length;
  const purchasedContacts = contacts.filter(c => c.status === 'purchased').length;
  const pendingContacts = contacts.filter(c => c.status === 'pending').length;
  const totalCommission = contacts
    .filter(c => c.status === 'purchased' && c.amount !== undefined)
    .reduce((sum, contact) => sum + (contact.amount || 0), 0);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 500, color: '#1a237e' }}>
          Bienvenido, {userData?.name}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Panel de control de ImmunoWorld
        </Typography>
      </Box>
      
      {/* Tarjetas de estadísticas */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ bgcolor: 'rgba(25, 118, 210, 0.1)', borderRadius: '50%', p: 1.5, mr: 2, display: 'flex' }}>
                  <PersonIcon sx={{ color: 'primary.main' }} />
                </Box>
                <Typography variant="h6" color="text.secondary">Contactos</Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 500 }}>{totalContacts}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Total de contactos registrados</Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ bgcolor: 'rgba(76, 175, 80, 0.1)', borderRadius: '50%', p: 1.5, mr: 2, display: 'flex' }}>
                  <ShoppingCartIcon sx={{ color: 'success.main' }} />
                </Box>
                <Typography variant="h6" color="text.secondary">Compras</Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 500 }}>{purchasedContacts}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Contactos que han comprado</Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ bgcolor: 'rgba(255, 152, 0, 0.1)', borderRadius: '50%', p: 1.5, mr: 2, display: 'flex' }}>
                  <FilterListIcon sx={{ color: 'warning.main' }} />
                </Box>
                <Typography variant="h6" color="text.secondary">Pendientes</Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 500 }}>{pendingContacts}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Contactos en seguimiento</Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ bgcolor: 'rgba(156, 39, 176, 0.1)', borderRadius: '50%', p: 1.5, mr: 2, display: 'flex' }}>
                  <MonetizationOnIcon sx={{ color: 'secondary.main' }} />
                </Box>
                <Typography variant="h6" color="text.secondary">Comisiones</Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 500 }}>{totalCommission.toFixed(2)} €</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Total de comisiones generadas</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Información y QR */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, color: '#1a237e' }}>Mi QR</Typography>
              <Divider sx={{ mb: 3 }} />
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <QRCodeSVG value={publicUrl} size={180} />
              </Box>
              <Box sx={{ mt: 2, p: 1, bgcolor: '#f5f5f5', borderRadius: 1, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                  {publicUrl}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={8}>
          <Card sx={{ height: '100%', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, color: '#1a237e' }}>Información de Usuario</Typography>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">Nombre</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{userData?.name}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">Rol</Typography>
                    <Chip 
                      label={userData?.role === 'admin' ? 'Administrador' : 
                             userData?.role === 'consultor' ? 'Consultor' : 'Colaborador'} 
                      size="small"
                      color={userData?.role === 'admin' ? 'secondary' : 
                             userData?.role === 'consultor' ? 'primary' : 'default'}
                      sx={{ fontWeight: 500 }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                    <Typography variant="body1">{userData?.email}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">Teléfono</Typography>
                    <Typography variant="body1">{userData?.phone}</Typography>
                  </Box>
                </Grid>
                {userData?.role === 'consultor' && (
                  <Grid item xs={12}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary">URL Tienda Immunocal</Typography>
                      <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>{userData?.immunocalUrl}</Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Enlace a la página de clientes */}
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <Card 
          sx={{ 
            p: 2, 
            cursor: 'pointer', 
            boxShadow: '0 2px 10px rgba(0,0,0,0.08)', 
            borderRadius: 2,
            maxWidth: 'fit-content',
            '&:hover': {
              boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
              transform: 'translateY(-2px)',
              transition: 'all 0.3s ease'
            }
          }}
          onClick={() => navigate('/dashboard/clientes')}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ClientesIcon sx={{ color: 'primary.main', fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main' }}>
              Ver todos mis clientes
            </Typography>
          </Box>
        </Card>
      </Box>
    </Box>
  );
};
